import { Button, TextField } from '@biq/apex-ui';
import { javascript } from '@codemirror/lang-javascript';
import { Grid, Typography } from '@mui/material';
import { dracula } from '@uiw/codemirror-theme-dracula';
import CodeMirror from '@uiw/react-codemirror';
import React, { useEffect, useState } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { FunctionParametersEmptyStateIcon } from '../../../../assets';
import { styles } from '../../../../components/create-form-feature-dialog/create-form-feature-dialog-helper';
import { PairedInputSelectionWidget } from '../../../../components/paired-input-selection-widget/PairedInputSelectionWidget';
import ReadOnlyWrapper from '../../../../components/read-only-wrapper/ReadOnlyWrapper';
import {
    BREAKPOINT_KEYS,
    CodeMirrorStyles,
    useMinWidthMediaQueries,
    validationsMessages,
} from '../../../../lib';
import { AttributeAssociationItem } from '../../../edge-definitions/edge-definition-helper';
import JobExecutor from '../job-executor/JobExecutor';
import {
    executeContractContainerStyles,
    ExecuteSmartContractJobFormInputs,
    executeSmartContractJobStyles,
} from './create-execute-smart-contract-job-helper';

interface ExecuteSmartContractFormProps {
    control: Control<ExecuteSmartContractJobFormInputs, any>;
    errors: FieldErrors<ExecuteSmartContractJobFormInputs>;
    handleSubmit: () => void;
    encodedQuery?: string;
    onFunctionChange: (func: string) => void;
    attributeAssociationList: AttributeAssociationItem[];
    handleAttributeAssociationsChanged: (
        list: AttributeAssociationItem[]
    ) => void;
}

export const ExecuteSmartContractForm = ({
    control,
    errors,
    encodedQuery = '',
    onFunctionChange,
    attributeAssociationList,
    handleAttributeAssociationsChanged,
    handleSubmit,
}: ExecuteSmartContractFormProps): React.JSX.Element => {
    const [deserializedCode, setDeserializedCode] = useState<string>('');
    const isMediaBreakpoint: boolean = useMinWidthMediaQueries(
        BREAKPOINT_KEYS.LG
    );

    useEffect(() => {
        if (encodedQuery) {
            setDeserializedCode(atob(encodedQuery));
        }
    }, [encodedQuery]);

    return (
        <Grid
            sx={
                isMediaBreakpoint
                    ? executeContractContainerStyles.executeContractContainer
                    : executeSmartContractJobStyles.minimizedExecuteContractContainer
            }
        >
            <Grid
                sx={
                    executeSmartContractJobStyles.smartContractFormSectionContainer
                }
            >
                <Grid
                    sx={
                        executeSmartContractJobStyles.functionDefinitionItemContainer
                    }
                >
                    <JobExecutor />
                </Grid>
                <Grid
                    sx={
                        executeSmartContractJobStyles.functionDefinitionLabelContainer
                    }
                >
                    <Typography
                        sx={
                            executeSmartContractJobStyles.executeContractSectionLabel
                        }
                    >
                        Function Definition
                    </Typography>
                </Grid>
                <Grid
                    sx={
                        executeSmartContractJobStyles.functionDefinitionItemInputContainer
                    }
                >
                    <Controller
                        name='func'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                required
                                label='Function'
                                error={!!errors.func}
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ): void => onFunctionChange(e.target.value)}
                                errorText={
                                    errors.func
                                        ? validationsMessages.required
                                        : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={
                        executeSmartContractJobStyles.functionDefinitionItemContainer
                    }
                >
                    <PairedInputSelectionWidget
                        attributeAssociationList={attributeAssociationList}
                        handleAttributeAssociationsChanged={
                            handleAttributeAssociationsChanged
                        }
                        title={'Parameters'}
                        addButtonLabel={'Parameter'}
                        duplicateErrorMessage={''}
                        emptyErrorMessage={validationsMessages.required}
                        leftSideInputLabel={'Parameter'}
                        rightSideInputLabel={'Value'}
                        checkForDuplicates={false}
                        leftSideInputStyles={
                            executeSmartContractJobStyles.functionParameterInput
                        }
                        rightSideInputStyles={
                            executeSmartContractJobStyles.functionValueInput
                        }
                        customInputSelectionStyles={
                            executeSmartContractJobStyles.functionParamsContainer
                        }
                        displayEmptyState
                        emptyStateHeadingText={'No Parameters'}
                        emptyStateBodyText={
                            'Add a parameter using the button in the top left of this panel.'
                        }
                        emptyStateIcon={FunctionParametersEmptyStateIcon}
                        emptyStateHeadingTextStyles={styles.headingText}
                        emptyStateBodyTextStyles={
                            executeSmartContractJobStyles.functionWidgetIconBodyText
                        }
                        emptyStateMainContainerStyles={
                            executeSmartContractJobStyles.functionWidgetEmptyStateContainer
                        }
                        emptyStateHeadingTextContainerStyles={
                            executeSmartContractJobStyles.headingTextContainer
                        }
                        emptyStateBodyTextContainerStyles={
                            executeSmartContractJobStyles.emptyStateBodyTextContainer
                        }
                        emptyStateItemContainerStyles={
                            executeSmartContractJobStyles.functionWidgetEmptyStateItemContainer
                        }
                        emptyStateIconStyles={
                            executeSmartContractJobStyles.functionWidgetIcon
                        }
                        addButtonContainerStyles={
                            executeSmartContractJobStyles.addParameterButtonContainer
                        }
                        addButtonStyles={
                            executeSmartContractJobStyles.addParameterButton
                        }
                        titleContainerStyles={
                            executeSmartContractJobStyles.titleContainer
                        }
                        widgetMainContainerStyles={
                            executeSmartContractJobStyles.functionWidgetContainer
                        }
                        deleteButtonContainerStyles={
                            executeSmartContractJobStyles.functionParamsDeleteButtonContainer
                        }
                        deleteButtonStyles={
                            executeSmartContractJobStyles.functionParamsDeleteButton
                        }
                    />
                </Grid>
                <Grid sx={executeSmartContractJobStyles.executeButtonContainer}>
                    <Button size='small' onClick={handleSubmit}>
                        Execute Contract
                    </Button>
                </Grid>
            </Grid>
            <Grid>
                <Typography sx={executeSmartContractJobStyles.codeMirrorLabel}>
                    Smart Contract
                </Typography>
                <ReadOnlyWrapper>
                    <CodeMirror
                        height={executeSmartContractJobStyles.codeMirrorHeight}
                        width={executeSmartContractJobStyles.codeMirrorWidth}
                        value={deserializedCode}
                        extensions={[
                            javascript({ jsx: true }),
                            CodeMirrorStyles,
                        ]}
                        theme={dracula}
                        readOnly
                    />
                </ReadOnlyWrapper>
            </Grid>
        </Grid>
    );
};
